import { KImage, KScrollView, KText, KView } from '@/component/RtlLayout';
import React from 'react';
import { PageRowData, Sku } from '../../../../api';
import { Toast } from '@kibt/meta-ui-krn';
import { $k18n } from '@/common/i18n';
import ReduceUnablePng from '@/assets/icon_detail/reduce_unable.png';
import ReducePng from '@/assets/icon_detail/reduce.png';
import IncreaseUnablePng from '@/assets/icon_detail/increase_unable.png';
import IncreasePng from '@/assets/icon_detail/increase.png';
import { logClickSelectNumber, logShowMessage } from '../../../../logger2.0';
import { scaleW } from '@/common/style';
import { useQuery } from '@/hooks/useQuery';
import { useDetailPageStore } from '@/page/detail/store';
import { KTouchableWithoutFeedback } from '@/component/RtlLayout/Touchable';

interface SpecSelectAreaProps {
    pageRawData: PageRowData;
    selectedKV: { [key: string]: number };
    selectItem: Function;
    immediateAvailableQuantity: number;
    currentSku: Sku;
    purchaseQuantity: number;
    setPurchaseQuantity: Function;
    showChangeCount: boolean;
    showUpStepper: boolean;
}
export function SpecSelectArea(props: SpecSelectAreaProps) {
    const {
        pageRawData = {} as PageRowData,
        selectedKV,
        selectItem,
        immediateAvailableQuantity,
        currentSku,
        purchaseQuantity,
        setPurchaseQuantity,
        showChangeCount,
        showUpStepper,
    } = props;
    const minNum = (pageRawData?.minPurchase as number) ?? 1;
    const isToBDetail = useQuery('pageType') === 'seller';
    const isSendSampleOrder = isToBDetail && useDetailPageStore?.getState()?.showSampleButton; // 1元寄样订单

    const isActive = (item, type) => {
        const key = type?.specId;
        const value = item?.specValueId;
        return key && value && selectedKV[key] === value;
    };

    const skuInfo = (
        pageRawData.skus
            ? pageRawData.skus.map((sku: { skuSpecs: any[] }) => {
                  const skuInfoItem = {};
                  sku.skuSpecs.forEach(spec => {
                      skuInfoItem[spec.specId] = spec.specValueId;
                  });
                  return {
                      ...skuInfoItem,
                      ...sku,
                  };
              })
            : []
    ) as Sku[];

    const isDisabled = (item, type) => {
        // 自营测品 放开限制
        if (pageRawData?.extraInfo?.selfOperated && type === 'cart') {
            return false;
        }
        const key = type?.specId;
        const value = item?.specValueId;
        const selectedReplaced = JSON.parse(JSON.stringify(selectedKV));
        if (key) selectedReplaced[key] = value;

        const flag = skuInfo.some(skuItem => {
            return Object.keys(selectedReplaced).every(selectKey => {
                if (selectedReplaced[selectKey]) {
                    // 库存小于最低加购时，不能选
                    return skuItem[selectKey] === selectedReplaced[selectKey] && skuItem.availableQuantity >= minNum;
                }
                return true;
            });
        });

        return !flag;
    };

    const descPurchaseQuantity = () => {
        // logClickBuyNow(1);
        logClickSelectNumber('0');
        if (purchaseQuantity !== 1 && purchaseQuantity <= minNum) {
            Toast.show({ text: $k18n({ id: 'k_251870', text: '最少购买{x}件', params: { x: minNum } }) });
            return;
        }
        if (purchaseQuantity === 1) {
            logShowMessage(pageRawData.itemId, 1);
            Toast.show({
                text: $k18n('商品不能再少啦'),
                duration: 1000,
            });
            return;
        }
        const newPurchaseQuantity = purchaseQuantity - 1;
        setPurchaseQuantity(newPurchaseQuantity);
    };

    const addPurchaseQuantity = () => {
        // 1元寄样订单 无法增加购买数量
        if (isSendSampleOrder) {
            return;
        }
        if (!(currentSku && currentSku.skuId)) {
            Toast.show({ text: $k18n({ id: 'k_471961', text: '请先选择规格' }) });
            return;
        }
        // 如果库存为0 或者库存小于最低加购数量
        if (Number(immediateAvailableQuantity) === 0 || Number(immediateAvailableQuantity) < minNum) {
            Toast.show({
                text: $k18n('抱歉，商品已售罄'),
                duration: 1000,
            });
            return;
        }
        logClickSelectNumber('1');
        // 如果选中了具体的一个sku， 则购买上限不能超过库存
        if (currentSku && currentSku.availableQuantity) {
            if (purchaseQuantity >= currentSku.availableQuantity) {
                setPurchaseQuantity(currentSku.availableQuantity);
                Toast.show({
                    text: $k18n('商品数量超出范围'),
                    duration: 1000,
                });
                logShowMessage(pageRawData.itemId, 2);
                return;
            }
        }
        // 有秒杀活动时，如果秒杀限购库存小于商品库存，单独toast限购提示文案
        if (
            pageRawData?.activityInfo?.skuLimitCount?.[currentSku?.skuId] > 0 &&
            pageRawData?.activityInfo?.skuLimitCount?.[currentSku?.skuId] < immediateAvailableQuantity &&
            purchaseQuantity >= pageRawData?.activityInfo?.skuLimitCount?.[currentSku?.skuId]
        ) {
            Toast.show({
                text: $k18n({ id: 'k_367077', params: { num: pageRawData?.activityInfo?.skuLimitCount?.[currentSku?.skuId] }, text: '累计限购XXX' }),
                duration: 1000,
            });
            return;
        }
        if (purchaseQuantity >= Number(immediateAvailableQuantity)) {
            setPurchaseQuantity(Number(immediateAvailableQuantity));
            Toast.show({
                text: $k18n('商品数量超出范围'),
                duration: 1000,
            });
            return;
        }
        const newPurchaseQuantity = purchaseQuantity + 1;
        setPurchaseQuantity(newPurchaseQuantity);
    };

    return (
        <KScrollView rtlClassName={'types'} showsVerticalScrollIndicator={false}>
            {pageRawData.specs?.map((spec, level) => {
                return (
                    <KView key={level} rtlClassName={'type'}>
                        <KView rtlClassName={'type_title'}>
                            <KText rtlClassName={'type_title_text'}> {spec.specName}</KText>
                        </KView>
                        <KView rtlClassName={'type_options'}>
                            {spec.itemSpecValues.map((item, index) => {
                                return (
                                    <KTouchableWithoutFeedback onPress={() => selectItem(item, spec, level, index, isDisabled(item, spec))}>
                                        <KView rtlClassName={['type_option', isActive(item, spec) ? 'type-option__active' : '']}>
                                            <KText
                                                rtlClassName={[
                                                    'type_option_text',
                                                    isActive(item, spec) ? 'type-text_active' : '',
                                                    isDisabled(item, spec) ? 'type-option__disabled' : '',
                                                ]}>
                                                {item.specValueName}
                                            </KText>
                                        </KView>
                                    </KTouchableWithoutFeedback>
                                );
                            })}
                        </KView>
                    </KView>
                );
            })}
            {showChangeCount && !showUpStepper && !pageRawData?.extraInfo?.selfOperated ? (
                <KView
                    rtlClassName={'optionbar_num'}
                    style={{"borderColor":"#f2f2f2","borderTopWidth":1}}>
                    <KView rtlClassName={'optionbar_num_left'}>
                        <KText rtlClassName={'optionbar_num_left-text'}>{$k18n('数量')}</KText>
                    </KView>
                    <KView rtlClassName={'optionbar_num_right'}>
                        {purchaseQuantity <= minNum ? (
                            <KTouchableWithoutFeedback onPress={() => descPurchaseQuantity()}>
                                <KView>
                                    <KImage source={ReduceUnablePng} style={{"width":"4.830917874396135rem","height":"4.830917874396135rem"}} />
                                </KView>
                            </KTouchableWithoutFeedback>
                        ) : (
                            <KTouchableWithoutFeedback onPress={() => descPurchaseQuantity()}>
                                <KView>
                                    <KImage source={ReducePng} style={{"width":"4.830917874396135rem","height":"4.830917874396135rem"}} />
                                </KView>
                            </KTouchableWithoutFeedback>
                        )}
                        <KView
                            style={{"borderRadius":"1.9323671497584543rem","overflow":"hidden","marginLeft":"1.9323671497584543rem","marginRight":"1.9323671497584543rem","backgroundColor":"rgba(200, 200, 200, 0.24)","width":"11.594202898550726rem"}}>
                            <KText
                                rtlClassName={'optionbar_num_right_num'}
                                style={{"borderRadius":"1.9323671497584543rem"}}>
                                {/* 1元寄样订单 购买数量限定是1  */}
                                {isSendSampleOrder ? 1 : purchaseQuantity}
                            </KText>
                        </KView>
                        {(currentSku.availableQuantity && purchaseQuantity === currentSku.availableQuantity) ||
                        Number(immediateAvailableQuantity) === 0 ||
                        Number(immediateAvailableQuantity) <= minNum ||
                        isSendSampleOrder ? (
                            <KTouchableWithoutFeedback onPress={() => addPurchaseQuantity()}>
                                <KView>
                                    <KImage source={IncreaseUnablePng} style={{"width":"4.830917874396135rem","height":"4.830917874396135rem"}} />
                                </KView>
                            </KTouchableWithoutFeedback>
                        ) : (
                            <KTouchableWithoutFeedback onPress={() => addPurchaseQuantity()}>
                                <KView>
                                    <KImage source={IncreasePng} style={{"width":"4.830917874396135rem","height":"4.830917874396135rem"}} />
                                </KView>
                            </KTouchableWithoutFeedback>
                        )}
                    </KView>
                </KView>
            ) : null}
        </KScrollView>
    );
}
